import React from 'react';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import { List } from '@wikifactory/voxel-ui';
import { Link, routerStore } from 'core/routing';
import { Section } from 'legacyComponents/layout/section';
import { CenteredButtonWrapper, ButtonShowMore } from 'apps/components/shared';
import { NoDataCard } from 'legacyComponents/cards/types/noData';
import { Button, EmptyDescription, EmptyText, EmptyWrapper } from 'components';
import { OperateChecker } from 'apps/components';
import { themes } from 'styles/themes/wikifactoryNew';
import {
  FileEmptyIcon,
  OpenTaskEmptyIcon,
  ProjectEmptyIcon,
  SharedFileEmptyIcon,
} from 'icons';
import { getLabelByRouteName } from 'apps/routes/utils';
import { clientStore } from 'apps/client/app';
import { isInitiativeSlug } from 'utils';
import { ViewTypeEnum } from 'types';
import { EmptyProjectWithCreate } from './EmptyProjectWithCreate';

type CardSectionProps = {
  title?: string;
  tooltip?: string;
  index?: number;
  gridColumns?: number;
  mediumColumns?: number;
  gridGap?: string;
  gridGaps?: string;
  updateHeader?: boolean;
  subTitle?: string;
  createHandler?: any;
  last?: boolean;
  first?: boolean;
  cwdFiles?: any[];
  owner?: boolean;
  isCollection?: boolean;
  isChannel?: boolean;
  isWhitelabel?: boolean;
  spaceType?: string;
  ownerName?: string;
  cardApp: any;
  CardComponent: React.ComponentType<any>;
  noDataCardType?: string;
  invisible?: boolean;
  introComponent?: any;
  t?: any;
  showButton?: boolean;
  noSort?: boolean;
  noCount?: boolean;
  initiative?: boolean;
  noMarginTop?: boolean;
  viewType?: ViewTypeEnum | string;
  customClearSortInfo?: boolean;
  actions?: React.ReactNode;
  intercomTarget?: string;
  maxItems?: number;
  showAllRouteName?: string;
  showAllRouteParams?: any;
  canShowNoDataCard?: boolean;
  dataTestId?: string;
  wrapperStyle?: React.CSSProperties;
  headerWrapperStyle?: React.CSSProperties;
};

@withNamespaces()
@observer
export class CardSection extends React.Component<
  CardSectionProps,
  Record<string, any>
> {
  render() {
    const {
      viewType,
      isWhitelabel,
      isCollection,
      isChannel,
      spaceType,
      owner,
      ownerName,
      cardApp,
      index,
      updateHeader,
      CardComponent,
      noDataCardType,
      title,
      noCount,
      subTitle,
      tooltip,
      noSort = false,
      first,
      last,
      invisible,
      createHandler,
      introComponent,
      initiative,
      showButton = true,
      noMarginTop,
      actions,
      gridColumns,
      mediumColumns,
      gridGap,
      gridGaps,
      intercomTarget = '',
      maxItems,
      showAllRouteName,
      showAllRouteParams,
      canShowNoDataCard,
      dataTestId,
      cwdFiles,
      wrapperStyle,
      headerWrapperStyle,
      customClearSortInfo,
    } = this.props;
    const cards = [];

    const { route } = routerStore;
    const { teamsQueryStatus, slugFromRouteOrSpace } = clientStore;
    const loadingTeams =
      teamsQueryStatus === 'loading' && isInitiativeSlug(slugFromRouteOrSpace);

    const emptyText = `No ${getLabelByRouteName(route.name) || 'Data'} yet.`;

    const placeholderRendererMap: Record<string, any> = {
      sharedFile: () => {
        if (route.name === 'workspace.sharedWithFiles') {
          return (
            <EmptyWrapper className='border border-gray-200 py-8 -mt-3'>
              <SharedFileEmptyIcon />
              <EmptyText text='No shared Files yet.' />
            </EmptyWrapper>
          );
        }
        if (route.name === 'workspace.spaceFiles') {
          return (
            <EmptyWrapper className='border border-gray-200 py-8 -mt-3'>
              <FileEmptyIcon />
              <EmptyText text={emptyText} />
              <OperateChecker type='canCreateIn'>
                <>
                  <Button
                    outline
                    size='small'
                    text='Upload File'
                    style={{
                      fontSize: 16,
                      borderColor: themes.color.wfBlack,
                    }}
                    iconPosition='right'
                    icon='add'
                    variant='tertiary'
                    onClick={() => {
                      routerStore.navigate(
                        'workspace.share-file',
                        route.params,
                      );
                    }}
                    dataTestId='share-file-upload-file'
                  />
                </>
              </OperateChecker>
            </EmptyWrapper>
          );
        }
        return (
          <EmptyWrapper className='border border-gray-200 py-8 -mt-3'>
            <FileEmptyIcon />
            <EmptyText text='No Files yet.' />
          </EmptyWrapper>
        );
      },
      homeProject: () => {
        if (route.name === 'workspace.sharedWithProjects') {
          return (
            <EmptyWrapper className='border border-gray-200 py-8 -mt-3'>
              <ProjectEmptyIcon />
              <EmptyText text='No shared Projects yet.' />
            </EmptyWrapper>
          );
        }
        if (route.name === 'workspace.spaceProjects') {
          return <EmptyProjectWithCreate emptyText={emptyText} />;
        }

        return (
          <EmptyWrapper className='border border-gray-200 py-8 -mt-3'>
            <ProjectEmptyIcon />
            <EmptyText text='No Projects yet.' />
          </EmptyWrapper>
        );
      },
      issue: () => {
        return (
          <EmptyWrapper className=' bg-white'>
            <OpenTaskEmptyIcon />
            <EmptyText text='No Open Tasks' />
            <EmptyDescription
              className='mb-2'
              text={
                "There's a lot to learn from past tasks. That's why they will always be a click away."
              }
            />
          </EmptyWrapper>
        );
      },
    };

    const callToActions: any[] = [];
    if (cardApp.length === 0 && !cardApp.manager.loading) {
      if (
        canShowNoDataCard ||
        owner ||
        initiative ||
        spaceType ||
        loadingTeams
      ) {
        return (
          <Section
            title={title}
            tooltip={tooltip}
            index={index}
            updateHeader={updateHeader}
            subTitle={subTitle}
            last={last}
            sectionConfig={cardApp}
            createHandler={owner || isChannel ? createHandler : ''}
            gridColumns={0}
            gridGap={gridGap}
            gridGaps={gridGaps}
            wrapperStyle={wrapperStyle}
            headerWrapperStyle={headerWrapperStyle}
            noSort
            customClearSortInfo={customClearSortInfo}
            noCount
          >
            {placeholderRendererMap[noDataCardType]?.() || (
              <NoDataCard
                type={noDataCardType}
                owner={owner}
                ownerName={ownerName}
                isChannel={isChannel}
                isCollection={isCollection}
                isWhitelabel={isWhitelabel}
                spaceType={spaceType}
              />
            )}
          </Section>
        );
      }
      return null;
    }

    // eslint-disable-next-line
    for (const [cardNo, cardObj] of cardApp.entries()) {
      if (cards.length >= maxItems) break;

      if (!cardObj.deleting) {
        cards.push(
          <CardComponent
            key={cardObj.id}
            cardObject={cardObj}
            cardNo={cardNo}
            cwdFiles={cwdFiles}
            cardColumns={cardApp.cardLayout.layoutSpec.cardColumns}
            cardApp={cardApp}
          />,
        );
      }
    }
    if (cardApp.manager.loadingMore) {
      let numberOfNoDataItems = cardApp.manager.variables.batchSize;
      if (maxItems > 0)
        numberOfNoDataItems = Math.min(
          maxItems,
          cardApp.manager.variables.batchSize,
        );
      // eslint-disable-next-line
      for (let i = 0; i < numberOfNoDataItems; i++) {
        cards.push(
          <CardComponent
            cardObject={null}
            key={`no-data-${i}`}
            cardNo={i}
            cardColumns={cardApp.cardLayout.layoutSpec.cardColumns}
          />,
        );
      }
    }

    let bottomButton = null;
    if (!cardApp.manager.loading) {
      if (showAllRouteName && maxItems < cardApp.length) {
        bottomButton = (
          <CenteredButtonWrapper>
            <Link name={showAllRouteName} params={showAllRouteParams}>
              <Button
                size='default'
                variant='primary'
                outline
                text='Show all'
                icon='arrow_right'
                iconPosition='right'
              />
            </Link>
          </CenteredButtonWrapper>
        );
      } else if (cardApp.manager.hasNextPage && showButton) {
        bottomButton = (
          <div className='flex justify-center'>
            <ButtonShowMore
              data-testid='loadMore-button'
              size='default'
              variant='primary'
              outline
              style={{ marginTop: 0 }}
              text={<Trans>Show more</Trans>}
              onClick={() => cardApp.manager.loadMore()}
            />
          </div>
        );
      }
    }

    if (viewType === ViewTypeEnum.List) {
      return (
        <div style={{ marginTop: 16 }}>
          <List
            className='w-full'
            items={cards.map(item => {
              return {
                content: item,
                key: item.key,
                style: {
                  paddingLeft: 0,
                  paddingRight: 0,
                },
              };
            })}
          />
          <div className='mt-4'>
            {callToActions}
            {bottomButton}
          </div>
        </div>
      );
    }

    return (
      <>
        {introComponent || ''}
        <Section
          title={title}
          tooltip={tooltip}
          index={index}
          updateHeader={updateHeader}
          subTitle={subTitle}
          createHandler={owner || isChannel ? createHandler : ''}
          sectionConfig={cardApp}
          invisible={invisible}
          first={first}
          last={last}
          noSort={noSort || cardApp.length <= gridColumns}
          noCount={noCount}
          noMarginTop={noMarginTop}
          actions={actions}
          gridColumns={gridColumns}
          mediumColumns={mediumColumns}
          gridGap={gridGap}
          gridGaps={gridGaps}
          intercomTarget={intercomTarget}
          dataTestId={dataTestId}
          wrapperStyle={wrapperStyle}
          headerWrapperStyle={headerWrapperStyle}
          customClearSortInfo={customClearSortInfo}
        >
          {cards}
          {callToActions}
        </Section>
        {bottomButton}
      </>
    );
  }
}
